// src/contexts/HistoryContext.tsx
import React, { createContext, useState, useRef } from "react";

export interface Message {
    role: "user" | "assistant";
    content: string;
}

interface Histories {
    [stepId: number]: Message[];
}

interface HistoryContextType {
    historiesRef: React.MutableRefObject<Histories>;
    addMessage: (stepId: number, message: Message) => void;
    getHistory: (stepId: number) => Message[];
    resetHistory: (stepId: number) => void;
    resetAllHistories: () => void;
}

export const HistoryContext = createContext<HistoryContextType | undefined>(
    undefined
);

export const HistoryProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const historiesRef = useRef<Histories>({});

    const addMessage = (stepId: number, message: Message) => {
        const currentHistories = historiesRef.current;
        const stepHistory = currentHistories[stepId] || [];
        historiesRef.current = {
            ...currentHistories,
            [stepId]: [...stepHistory, message],
        };
    };

    const getHistory = (stepId: number): Message[] => {
        return historiesRef.current[stepId] || [];
    };

    const resetHistory = (stepId: number) => {
        const currentHistories = historiesRef.current;
        const { [stepId]: _, ...rest } = currentHistories;
        historiesRef.current = rest;
    };

    const resetAllHistories = () => {
        historiesRef.current = {};
    };

    return (
        <HistoryContext.Provider
            value={{
                historiesRef,
                addMessage,
                getHistory,
                resetHistory,
                resetAllHistories,
            }}>
            {children}
        </HistoryContext.Provider>
    );
};
